// src/app/shared/app-init/AppInitializationController.ts
import { makeObservable, observable, action, runInAction } from 'mobx';
import { SingletonClass } from '@/app/utils/SingletonClass';
import { OmegaWsService } from '@/sdk/OmegaWsService';
import { AuthApi } from '@/app/data/auth/AuthApi';
import { getInitData } from '@/app/network/utils/TelegramAuthUtil';

export enum InitializationState {
    VALIDATING = 'validating',
    CONNECTING = 'connecting',
    READY = 'ready',
    VALIDATION_ERROR = 'validation_error',
}

export class AppInitializationController extends SingletonClass {
    private static _instance: AppInitializationController;
    private omegaService = OmegaWsService.getInstance();

    state: InitializationState = InitializationState.VALIDATING;
    validationError: string | null = null;
    wsError: string | null = null;

    private isInitialized = false;
    private cleanupFns: (() => void)[] = [];

    static getInstance(): AppInitializationController {
        if (!AppInitializationController._instance) {
            AppInitializationController._instance = new AppInitializationController();
        }
        return AppInitializationController._instance;
    }

    constructor() {
        super();
        makeObservable(this, {
            state: observable,
            validationError: observable,
            wsError: observable,
            retry: action,
        });
    }

    initialize(): void {
        if (this.isInitialized) return;
        this.isInitialized = true;

        runInAction(() => {
            this.state = InitializationState.VALIDATING;
            this.validationError = null;
            this.wsError = null;
        });

        const client = this.omegaService.getOrCreateClient();
        this.cleanupFns.push(
            client.on('status', (status: string) => {
                if (status === 'ready') {
                    this.handleConnected();
                }
            }),
            client.on('error', (error: unknown) => {
                console.error('[OmegaWs] error', error);
                this.handleWsError(error instanceof Error ? error.message : 'WebSocket error');
            })
        );

        this.connectWebSocket();
    }

    private async connectWebSocket(): Promise<void> {
    runInAction(() => {
        this.state = InitializationState.CONNECTING;
    });

    try {
        const token = AuthApi.getAccessToken();
        if (token) {
            await this.omegaService.connect();
        } else {
            const initData = getInitData();
            if (!initData) {
                // Нет ни токена, ни initData — это фатально
                runInAction(() => {
                    this.state = InitializationState.VALIDATION_ERROR;
                    this.validationError = 'Нет данных для входа';
                });
                return;
            }
            await this.omegaService.connect(initData);
        }
    } catch (error) {
        // Если пользователь в Telegram — падаем в VALIDATION_ERROR, а не в READY
        const isTelegram = typeof window !== 'undefined' && (window as any).Telegram?.WebApp;
        if (isTelegram) {
            runInAction(() => {
                this.state = InitializationState.VALIDATION_ERROR;
                this.validationError = error instanceof Error ? error.message : 'Ошибка авторизации';
            });
        } else {
            // Для веба — показываем интерфейс с баннером
            this.forceReadyWithError(error);
        }
    }
}

    private handleConnected(): void {
        let attempts = 0;
        const maxAttempts = 30; // ~3 сек

        const tryReady = () => {
            const token = AuthApi.getAccessToken();

            if (token) {
                runInAction(() => {
                    this.state = InitializationState.READY;
                    this.wsError = null;
                });
                return;
            }

            attempts += 1;
            if (attempts >= maxAttempts) {
                // Если токен так и не появился — считаем, что авторизация не удалась
                runInAction(() => {
                    this.state = InitializationState.VALIDATION_ERROR;
                    this.validationError = 'Не удалось получить токен авторизации';
                });
                return;
            }

            window.setTimeout(tryReady, 100);
        };

        tryReady();
    }

    private handleWsError(errorMessage: string): void {
        runInAction(() => {
            this.wsError = errorMessage;
        });
        // Баннер сам запустит восстановление
    }

    private forceReadyWithError(error: unknown): void {
        runInAction(() => {
            this.state = InitializationState.READY;
            this.wsError = error instanceof Error ? error.message : 'Ошибка подключения';
        });
    }

    /** Ручной retry из баннера (если понадобится) */
    retry = (): void => {
        this.omegaService.disconnect();
        runInAction(() => {
            this.state = InitializationState.VALIDATING;
            this.validationError = null;
            this.wsError = null;
        });
        this.connectWebSocket();
    };

    reconnectWebSocketQuick = async (): Promise<void> => {
        try {
            await this.omegaService.reconnect();
            runInAction(() => { this.wsError = null; });
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Reconnect failed';
            runInAction(() => { this.wsError = msg; });
            throw error;
        }
    };

    dispose(): void {
        this.cleanupFns.forEach(fn => fn());
        this.cleanupFns = [];
        this.omegaService.destroy();
        this.isInitialized = false;
    }
}